import React, { useEffect, useState } from "react";
import { Inbox, Mail, Phone, Clock } from "lucide-react";
import api from "../../lib/api";
import { PageTitle, Card, Badge, Loading, fmtDate } from "./ui";

const STATUSES = ["new", "contacted", "closed"];

export default function Inquiries() {
  const [inquiries, setInquiries] = useState(null);
  const [stats, setStats] = useState(null);

  const load = () => {
    api.get("/admin/inquiries").then((r) => setInquiries(r.data)).catch(() => setInquiries([]));
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  };

  useEffect(load, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/inquiries/${id}`, { status });
    load();
  };

  if (!inquiries) return <Loading />;

  return (
    <div data-testid="inquiries-page">
      <PageTitle eyebrow="Administration" title="Prospective-Member Inquiries" sub="Submissions from the public inquiry form. Admin access only." />

      {stats && (
        <div className="mb-8 grid gap-4 sm:grid-cols-3">
          <Card><p className="font-sans text-xs uppercase tracking-widest text-gold-deep">Total Inquiries</p><p className="mt-2 font-display text-4xl text-sapphire-deep">{stats.total_inquiries}</p></Card>
          <Card><p className="font-sans text-xs uppercase tracking-widest text-gold-deep">Awaiting Response</p><p className="mt-2 font-display text-4xl text-sapphire-deep">{stats.new_inquiries}</p></Card>
          <Card><p className="font-sans text-xs uppercase tracking-widest text-gold-deep">Members</p><p className="mt-2 font-display text-4xl text-sapphire-deep">{stats.members}</p></Card>
        </div>
      )}

      {inquiries.length === 0 ? (
        <Card className="flex flex-col items-center py-16 text-center">
          <Inbox size={40} className="text-gold-deep" />
          <p className="mt-4 font-display text-2xl text-sapphire-deep">No inquiries yet</p>
          <p className="mt-1 font-sans text-sm text-ink/55">New submissions from the public site will appear here.</p>
        </Card>
      ) : (
        <div className="grid gap-5">
          {inquiries.map((q) => (
            <Card key={q.id} data-testid={`inquiry-row-${q.id}`}>
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-display text-xl font-semibold text-sapphire-deep">{q.name}</h3>
                    <Badge>{q.status}</Badge>
                    <span className="flex items-center gap-1 font-sans text-xs text-ink/45"><Clock size={12} /> {fmtDate(q.created_at, true)}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 font-sans text-sm text-ink/65">
                    <a href={`mailto:${q.email}`} className="flex items-center gap-1 hover:text-gold-deep"><Mail size={13} /> {q.email}</a>
                    {q.phone && <span className="flex items-center gap-1"><Phone size={13} /> {q.phone}</span>}
                    {q.heard_from && <span>Heard via: {q.heard_from}</span>}
                  </div>
                  <p className="mt-3 rounded-xl bg-sapphire-deep/[0.04] p-4 font-sans text-sm text-ink/75">{q.message}</p>
                </div>
                <div className="flex gap-2 md:flex-col">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(q.id, s)}
                      className={`rounded-full px-4 py-2 font-sans text-xs font-semibold uppercase tracking-widest transition ${
                        q.status === s ? "bg-gold text-sapphire-deep" : "border border-sapphire-deep/15 text-ink/55 hover:border-gold hover:text-gold-deep"
                      }`}
                      data-testid={`inquiry-status-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
